import {right} from "@popperjs/core";
import {AxiosError} from "axios";
import {PageHeader} from "components/src//layout/PageHeader.tsx";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {DLPApi, DLPPolicyPattern} from "components/src/api/admin/generated";
import {Pager} from "components/src/common/Pager";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {SaveChangesRef} from "components/src/common/SaveChangesRef";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageAlert} from "components/src/layout/PageAlert.tsx";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageFooter} from "components/src/layout/PageFooter.tsx";
import {SettingsLevel} from "components/src/settings/SettingsUtils";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {
    CheckCircleSolid as InheritedIcon,
    DotsGrid3x3 as GroupIcon,
    Plus as AddIcon,
    ShieldEye as DLPIcon,
    Xmark as RemoveIcon
} from "iconoir-react";
import {Dispatch, forwardRef, ReactNode, SetStateAction, useEffect, useImperativeHandle, useState} from 'react'
import Col from "react-bootstrap/Col";
import Form from 'react-bootstrap/Form';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Row from "react-bootstrap/Row";
import Table from 'react-bootstrap/Table';
import Tooltip from "react-bootstrap/Tooltip";
import {useNavigate} from "react-router-dom";
import {DLPColumn} from "./DLPColumn";
import {DLPTableHeader} from "./DLPTableHeader";

/*
 * REST api for accessing the back-end
 */
const dlpApi = new DLPApi(ApiConfig.config());

interface DLPSelectionProps {
    settingsLevel : SettingsLevel
    settingsLevelSubSelector : string
    setShouldSave: Dispatch<SetStateAction<boolean>>
    setSaving : Dispatch<SetStateAction<boolean>>
    saveButton?: ReactNode
}

export const DLPSelection = forwardRef<SaveChangesRef | undefined, DLPSelectionProps> ((({
                settingsLevel,
                settingsLevelSubSelector,
                setShouldSave,
                setSaving,
                saveButton} : DLPSelectionProps, ref) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * All patterns
     */
    const [patterns, setPatterns] = useState<DLPPolicyPattern[]>([]);
    /**
     * The total number of patterns
     */
    const [patternsCount, setPatternsCount] = useState<number>(0);
    /**
     * The selected patterns
     */
    const [selectedPatterns, setSelectedPatterns] = useState<DLPPolicyPattern[]>([]);
    /**
     * The new active patterns
     */
    const [newActivePatterns, setNewActivePatterns] = useState<DLPPolicyPattern[]>([]);
    /**
     * The loaded active patterns
     */
    const [loadedActivePatterns, setLoadedActivePatterns] = useState<DLPPolicyPattern[]>([]);
    /**
     * The active patterns
     */
    const [selectedActivePatterns, setSelectedActivePatterns] = useState<DLPPolicyPattern[]>([]);
    /**
     * If true, all active patterns are reloaded
     */
    const [forceReloadActive, setForceReloadActive] = useState<boolean>(false);
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of domains to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    /**
     * Override the forwarded ref with the saveSettings method to allow the parent to save the modified settings
     */
    useImperativeHandle(ref, () => {
        return {
            saveChanges(){
                save();
            }
        }
    });

    useEffect(() =>
    {
        logger.debug("Load pattern count")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        dlpApi.dLPControllerGetPolicyPatternCount()
            .then(response => {
                setPatternsCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting pattern count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() =>
    {
        logger.debug("Load patterns")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        dlpApi.dLPControllerGetPolicyPatterns((currentPage - 1) * rowsPerPage, rowsPerPage)
            .then(response => {
                setPatterns(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting patterns", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        t
    ]);

    useEffect(() =>
    {
        logger.debug("Load active patterns")

        // reset state
        setForceReloadActive(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        let callback;

        switch (settingsLevel)
        {
            case SettingsLevel.User:
                callback = async () => {
                    return await dlpApi.dLPControllerGetUserPolicyPatterns(settingsLevelSubSelector);
                };
                break;
            case SettingsLevel.Domain:
                callback = async () => {
                    return await dlpApi.dLPControllerGetDomainPolicyPatterns(settingsLevelSubSelector);
                };
                break;
            case SettingsLevel.System:
                callback = async () => {
                    return await dlpApi.dLPControllerGetGlobalPolicyPatterns();
                };
                break;
        }

        if (callback === undefined) {
            throw new Error("callback is undefined");
        }

        callback().then(response => {
                setLoadedActivePatterns(response.data);
                setNewActivePatterns(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error loading active patterns", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        settingsLevel,
        settingsLevelSubSelector,
        forceReloadActive,
        t
    ]);

    useEffect(() =>
    {
        logger.debug("Update should save")

        // if the new active patterns are the same as the loaded patterns, saving is not needed
        setShouldSave(newActivePatterns.length != loadedActivePatterns.length ||
            !newActivePatterns.every(p1 => loadedActivePatterns.some(p2 => p1.name === p2.name)))

    }, [loadedActivePatterns,
        newActivePatterns,
        setShouldSave]);

    /**
     * Saves changes
     */
    const save = () =>
    {
        logger.debug("saving active patterns")

        // tell the parent that this component is saving (to show a spinner)
        setSaving(true);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        let callback;

        switch (settingsLevel)
        {
            case SettingsLevel.User:
                callback = async () => {
                    return await dlpApi.dLPControllerSetUserPolicyPatterns(settingsLevelSubSelector, newActivePatterns.map(p => p.name));
                };
                break;
            case SettingsLevel.Domain:
                callback = async () => {
                    return await dlpApi.dLPControllerSetDomainPolicyPatterns(settingsLevelSubSelector, newActivePatterns.map(p => p.name));
                };
                break;
            case SettingsLevel.System:
                callback = async () => {
                    return await dlpApi.dLPControllerSetGlobalPolicyPatterns(newActivePatterns.map(p => p.name));
                };
                break;
        }

        if (callback === undefined) {
            throw new Error("callback is undefined");
        }

        callback().then(() => {
            setForceReloadActive(true);
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error saving patterns", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() =>
        {
            setSaving(false);

            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }

    const onChangeSelectActivePatterns = (checked : boolean, pattern : DLPPolicyPattern) =>
    {
        setSelectedActivePatterns(checked ?
            [...selectedActivePatterns, pattern] :
            selectedActivePatterns.filter(p => p.name !== pattern.name)
        );
    }

    const onChangeSelectPatterns = (checked : boolean, pattern : DLPPolicyPattern) =>
    {
        setSelectedPatterns(checked ?
            [...selectedPatterns, pattern] :
            selectedPatterns.filter(p => p.name !== pattern.name)
        );
    }

    /**
     * An available pattern should be disabled if it is already a member of the active patterns
     */
    function isAvailablePatternDisabled(name : string) {
        return newActivePatterns.map(p => p.name).includes(name)
    }

    return (
        <>
            <PageHeader icon={<DLPIcon />} title={t("dlp.selection-title")}/>

            <PageAlert message={errorMessage} setMessage={setErrorMessage}/>

            <PageContent>
                <div className="fs-5 text-muted mb-2">{t("dlp.active-patterns")}</div>

                <Row className="w-100">
                    <Col>
                        <Table className="sticky sticky-x" bordered hover>
                            {selectedActivePatterns.length === 0 ?
                                <DLPTableHeader/>
                                :
                                <thead className="sticky-top">
                                <tr>
                                    <th colSpan={9}>
                                        <Navbar className="bg-body-tertiary ps-2">
                                            <Navbar.Text className="ms-2">{selectedActivePatterns.length} selected</Navbar.Text>

                                            <div className="vr ms-4 me-2"></div>

                                            <Nav>
                                                <Nav.Link onClick={() => {
                                                    setNewActivePatterns([...newActivePatterns].filter(p => !selectedActivePatterns.includes(p)));
                                                    setSelectedActivePatterns([])
                                                }}>
                                                    <RemoveIcon className="text-danger"/> {t("dlp.remove-selected")}
                                                </Nav.Link>
                                            </Nav>
                                        </Navbar>
                                    </th>
                                </tr>
                                </thead>
                            }
                            <tbody>
                            {newActivePatterns?.map((p: DLPPolicyPattern) => (
                                <tr key={p.name}>
                                    <DLPColumn className="td-w-min">
                                        <Form.Check
                                            type="checkbox"
                                            className="me-0"
                                            checked={selectedActivePatterns.includes(p)}
                                            onChange={(e) => onChangeSelectActivePatterns(e.target.checked, p)}
                                            inline
                                        />
                                        {p.inherited &&
                                            <OverlayTrigger
                                                placement={right}
                                                overlay={
                                                    <Tooltip>
                                                        {t("dlp.inherited")}
                                                    </Tooltip>
                                                }
                                            >
                                                <InheritedIcon className="text-warning table-icon-width table-icon-height ms-2"/>
                                            </OverlayTrigger>
                                        }
                                        {p.group &&
                                            <OverlayTrigger
                                                placement={right}
                                                overlay={
                                                    <Tooltip>
                                                        {t("dlp.group")}
                                                    </Tooltip>
                                                }
                                            >
                                                <GroupIcon className="text-success"/>
                                            </OverlayTrigger>
                                        }
                                    </DLPColumn>
                                    <DLPColumn value={p.name}/>
                                    <DLPColumn value={p.description}/>
                                    <DLPColumn value={p.regEx}/>
                                    <DLPColumn value={p.validator}/>
                                    <DLPColumn value={p.group ? "" : `${p.threshold}`}/>
                                    <DLPColumn value={p.action}/>
                                    <DLPColumn value={p.group ? "" : `${p.delayEvaluation}`}/>
                                    <DLPColumn value={p.matchFilter}/>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>

                <div className="fs-5 text-muted mb-2">{t("dlp.available-patterns")}</div>

                <Row className="w-100">
                    <Col>
                        <Table className="sticky sticky-x" bordered hover>
                            {selectedPatterns.length === 0 ?
                                <DLPTableHeader/>
                                :
                                <thead className="sticky-top">
                                <tr>
                                    <th colSpan={9}>
                                        <Navbar className="bg-body-tertiary ps-2">
                                            <Navbar.Text className="ms-2">{selectedPatterns.length} selected</Navbar.Text>

                                            <div className="vr ms-4 me-2"></div>

                                            <Nav>
                                                <Nav.Link onClick={() => {
                                                    setNewActivePatterns([...newActivePatterns, ...selectedPatterns]);
                                                    setSelectedPatterns([])
                                                }}>
                                                    <AddIcon className="text-success"/> {t("dlp.add-selected")}
                                                </Nav.Link>
                                            </Nav>
                                        </Navbar>
                                    </th>
                                </tr>
                                </thead>
                            }
                            <tbody>
                            {patterns.map((p: DLPPolicyPattern) => (
                                <tr key={p.name}
                                    className={(isAvailablePatternDisabled(p.name) ? "table-secondary" : "")}>
                                    <DLPColumn className="td-w-min">
                                        <Form.Check
                                            type="checkbox"
                                            className="me-0"
                                            checked={selectedPatterns.find((spattern) =>  spattern.name === p.name) !== undefined}
                                            onChange={(e) => onChangeSelectPatterns(e.target.checked, p)}
                                            disabled={isAvailablePatternDisabled(p.name)}
                                            inline
                                        />
                                        {p.group &&
                                            <OverlayTrigger
                                                placement={right}
                                                overlay={
                                                    <Tooltip>
                                                        {t("dlp.group")}
                                                    </Tooltip>
                                                }
                                            >
                                                <GroupIcon className="text-success"/>
                                            </OverlayTrigger>
                                        }
                                    </DLPColumn>
                                    <DLPColumn value={p.name}/>
                                    <DLPColumn value={p.description}/>
                                    <DLPColumn value={p.regEx}/>
                                    <DLPColumn value={p.validator}/>
                                    <DLPColumn value={p.group ? "" : `${p.threshold}`}/>
                                    <DLPColumn value={p.action}/>
                                    <DLPColumn value={p.group ? "" : `${p.delayEvaluation}`}/>
                                    <DLPColumn value={p.matchFilter}/>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </PageContent>

            <PageFooter
                left={<RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage}/>}
                right={
                <>
                    {saveButton}

                    <Pager itemCount={patternsCount ?? 0}
                              currentPage={currentPage}
                              setCurrentPage={setCurrentPage}
                              maxItemsPerPage={rowsPerPage}
                    />
                </>}
            />
        </>
    );
}))

// see https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/display-name.md why this is needed
DLPSelection.displayName="DLPSelection"
